<template>
    <div class="products-view">
        <PageTitle title="Gestion des produits" />

        <!-- Liste des produits (nouveau composant) -->
        <ProductsList
            :products="products"
            :pagination="pagination"
            :loading="loading"
            :categories="categories"
            :low-stock-products="lowStockProducts"
            @add-product="openAddModal"
            @edit-product="editProduct"
            @delete-product="confirmDelete"
            @page-change="handlePageChange"
            @search-change="handleSearchChange"
            @category-change="handleCategoryChange"
            @low-stock-toggle="handleLowStockToggle"
        />

        <!-- Modal d'ajout de produit -->
        <CreateProductModal :show="showAddModal" :categories="categories" @close="closeAddModal"
            @submit="handleProductCreate" />

        <!-- Modal de modification de produit -->
        <EditProductModal :show="showEditModal" :product="productForm" :categories="categories" @close="closeEditModal"
            @submit="handleProductUpdate" />

        <!-- Modal de confirmation de suppression -->
        <div v-if="showDeleteModal" class="modal-overlay">
            <div class="modal-container delete-modal">
                <div class="modal-header">
                    <h3>Confirmer la suppression</h3>
                    <button class="close-btn" @click="showDeleteModal = false">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div class="modal-body text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="text-danger mb-4">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                    <p>Êtes-vous sûr de vouloir supprimer le produit <strong>{{ productToDelete?.name }}</strong> ?</p>
                    <p class="text-danger">Cette action est irréversible.</p>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" @click="showDeleteModal = false">Annuler</button>
                    <button class="btn btn-danger" @click="deleteProduct" :disabled="deleteLoading">
                        <span v-if="deleteLoading" class="spinner-border spinner-border-sm mr-1"></span>
                        Supprimer
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { ref, reactive, onMounted } from 'vue';
import { productsService, categoriesService } from '@/services';
import PageTitle from '@/components/common/PageTitle.vue';
import { useToastStore } from '@/store/modules';
import ProductsList from '@/views/products/ProductsList.vue';
import CreateProductModal from '@/views/products/CreateProductModal.vue';
import EditProductModal from '@/views/products/EditProductModal.vue';

export default {
    name: 'ProductsView',
    components: {
        PageTitle,
        ProductsList,
        CreateProductModal,
        EditProductModal,
    },
    setup() {
        const toastStore = useToastStore();

        // Données des produits
        const products = ref([]);
        const lowStockProducts = ref([]);
        const categories = ref([]);

        // État de l'UI
        const loading = ref(false);
        const error = ref(null);
        const showAddModal = ref(false);
        const showEditModal = ref(false);
        const showDeleteModal = ref(false);
        const formSubmitting = ref(false);
        const deleteLoading = ref(false);
        const productToDelete = ref(null);
        const validationErrors = ref({});

        // Pagination
        const pagination = reactive({
            currentPage: 1,
            perPage: 10,
            total: 0,
            lastPage: 1,
            from: null,
            to: null
        });

        // Filtres
        const filters = reactive({
            search: '',
            category: '',
            active: true,
            sortBy: 'created_at',
            sortDirection: 'desc'
        });

        // Formulaire produit
        const productForm = reactive({
            name: '',
            description: '',
            price_excl_tax: '',
            tax_rate: 18,
            category_id: '',
            active: true,
            image: null
        });

        // Méthodes
        const loadProducts = async (options = {}) => {
            const { skipLowStock = false } = options;
            loading.value = true;
            error.value = null;

            try {
                // Préparer les paramètres pour l'API
                const params = {
                    page: pagination.currentPage,
                    per_page: pagination.perPage,
                    search: filters.search,
                    category_id: filters.category || undefined,
                    active: filters.active,
                    sort_by: filters.sortBy,
                    sort_direction: filters.sortDirection
                };

                // Appel au service
                const response = await productsService.getProducts(params);
                // Extraire les données
                if (response.data.current_page) {
                    // Format de pagination Laravel
                    products.value = response.data.data || [];
                    pagination.currentPage = response.data.current_page || 1;
                    pagination.perPage = response.data.per_page || 10;
                    pagination.total = response.data.total || 0;
                    pagination.lastPage = response.data.last_page || 1;
                    pagination.from = response.data.from ?? null;
                    pagination.to = response.data.to ?? null;
                } else if (response.data.meta) {
                    // Format avec meta
                    products.value = response.data.data || [];
                    pagination.currentPage = response.data.meta.current_page || 1;
                    pagination.perPage = response.data.meta.per_page || 10;
                    pagination.total = response.data.meta.total || 0;
                    pagination.lastPage = response.data.meta.last_page || 1;
                    pagination.from = response.data.meta.from ?? response.data.from ?? null;
                    pagination.to = response.data.meta.to ?? response.data.to ?? null;
                } else {
                    // Format simple
                    products.value = response.data.products || response.data || [];
                }

                // Charger les produits à faible stock sauf si on demande de sauter
                if (!skipLowStock) {
                    await fetchLowStockProducts();
                }
            } catch (err) {
                error.value = "Erreur lors du chargement des produits";
                console.error(err);

                // Afficher une notification d'erreur
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des produits.',
                    duration: 5000
                });
            } finally {
                loading.value = false;
            }
        };

        const fetchLowStockProducts = async () => {
            try {
                // Appel au service
                const response = await productsService.getLowStockProducts(5);
                lowStockProducts.value = response.data.products || response.data;
            } catch (err) {
                console.error("Erreur lors du chargement des produits à faible stock:", err);
                // En cas d'erreur, utiliser la méthode de filtrage local
                lowStockProducts.value = products.value.filter(product => product.stock <= 5);
            }
        };

        const fetchCategories = async () => {
            try {
                const response = await categoriesService.getCategories({
                    perPage: 100, // Récupérer un grand nombre de catégories
                    page: 1
                });

                if (response && response.data) {
                    if (response.data.data) {
                        // Format paginé
                        categories.value = response.data.data;
                    } else {
                        // Format simple
                        categories.value = response.data;
                    }
                }
            } catch (err) {
                console.error("Erreur lors du chargement des catégories:", err);

                // Afficher une notification d'erreur
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des catégories.',
                    duration: 5000
                });
            }
        };

        // Gestion des formulaires
        const closeAddModal = () => {
            showAddModal.value = false;
        };

        const closeEditModal = () => {
            showEditModal.value = false;
        };

        const closeModals = () => {
            showAddModal.value = false;
            showEditModal.value = false;
            showDeleteModal.value = false;
            resetProductForm();
        };

        const openAddModal = async () => {
            // S'assurer que les catégories sont chargées avant d'ouvrir le modal
            if (categories.value.length === 0) {
                await fetchCategories();
            }

            showAddModal.value = true;
        };

        const resetProductForm = () => {
            Object.assign(productForm, {
                name: '',
                description: '',
                price_excl_tax: '',
                tax_rate: 18,
                category_id: '',
                active: true,
                image: null,
                image_url: null
            });
            validationErrors.value = {};
        };

        // CRUD des produits
        const editProduct = async (product) => {
            // S'assurer que les catégories sont chargées avant d'ouvrir le modal
            if (categories.value.length === 0) {
                await fetchCategories();
            }

            try {
                // Récupérer les détails complets du produit depuis l'API
                const response = await productsService.getProduct(product.id);
                const fullProduct = response.data.product || response.data;

                // Copier les valeurs du produit dans le formulaire
                Object.assign(productForm, fullProduct);

                showEditModal.value = true;
            } catch (err) {
                console.error("Erreur lors de la récupération des détails du produit:", err);

                // Fallback: utiliser les données disponibles
                Object.assign(productForm, product);
                showEditModal.value = true;

                // Notification d'erreur
                toastStore.addToast({
                    type: 'warning',
                    message: 'Impossible de charger tous les détails du produit.',
                    duration: 4000
                });
            }
        };

        const handleProductCreate = (formData) => {
            createProduct(formData);
        };

        const handleProductUpdate = (formData) => {
            updateProduct(formData);
        };

        const handleProductSubmit = (formData) => {
            if (showEditModal.value) {
                updateProduct(formData);
            } else {
                createProduct(formData);
            }
        };

        const createProduct = async (formData) => {
            formSubmitting.value = true;

            try {
                const data = new FormData();

                // Ajouter tous les champs du formulaire
                Object.keys(formData).forEach(key => {
                    if (key !== 'image' && formData[key] !== null && formData[key] !== undefined) {
                        data.append(key, formData[key]);
                    }
                });

                // Ajouter l'image si présente
                if (formData.image) {
                    data.append('image', formData.image);
                }

                // Appel au service
                await productsService.createProduct(data);

                // Fermer le modal et réinitialiser le formulaire
                closeModals();

                // Afficher une notification de succès
                toastStore.addToast({
                    type: 'success',
                    message: 'Produit ajouté avec succès !'
                });

                // Recharger la liste des produits
                await loadProducts();
            } catch (err) {
                console.error("Erreur lors de la création du produit:", err);

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {};
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: 'Erreur lors de la création du produit'
                    });
                }
            } finally {
                formSubmitting.value = false;
            }
        };

        const updateProduct = async (formData) => {
            formSubmitting.value = true;

            try {
                const productId = productForm.id;
                const data = new FormData();

                // Ajouter tous les champs du formulaire
                Object.keys(formData).forEach(key => {
                    if (key !== 'image' && key !== 'id' && formData[key] !== null && formData[key] !== undefined) {
                        data.append(key, formData[key]);
                    }
                });

                // Ajouter l'image si présente et que c'est un nouveau fichier
                if (formData.image && formData.image instanceof File) {
                    data.append('image', formData.image);
                }

                // Appel au service
                await productsService.updateProduct(productId, data);

                // Fermer le modal et réinitialiser le formulaire
                closeModals();

                // Afficher une notification de succès
                toastStore.addToast({
                    type: 'success',
                    message: 'Produit mis à jour avec succès !'
                });

                // Recharger la liste des produits
                await loadProducts();
            } catch (err) {
                console.error("Erreur lors de la mise à jour du produit:", err);

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {};
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: 'Erreur lors de la mise à jour du produit'
                    });
                }
            } finally {
                formSubmitting.value = false;
            }
        };

        const confirmDelete = (product) => {
            productToDelete.value = product;
            showDeleteModal.value = true;
        };

        const deleteProduct = async () => {
            if (!productToDelete.value) return;

            deleteLoading.value = true;

            try {
                // Appel au service
                await productsService.deleteProduct(productToDelete.value.id);

                // Fermer le modal de confirmation
                showDeleteModal.value = false;
                productToDelete.value = null;

                // Afficher une notification de succès
                toastStore.addToast({
                    type: 'success',
                    message: 'Produit supprimé avec succès !'
                });

                // Recharger la liste des produits
                await loadProducts();
            } catch (err) {
                console.error("Erreur lors de la suppression du produit:", err);

                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la suppression du produit'
                });
            } finally {
                deleteLoading.value = false;
            }
        };

        // Gestionnaires d'événements pour ProductsList
        const handlePageChange = (page) => {
            pagination.currentPage = page;
            loadProducts({ skipLowStock: true });
        };

        const handleSearchChange = (searchTerm) => {
            filters.search = searchTerm || '';
            pagination.currentPage = 1;
            loadProducts();
        };

        const handleCategoryChange = (categoryId) => {
            filters.category = categoryId || '';
            pagination.currentPage = 1;
            loadProducts();
        };

        const handleLowStockToggle = (showLowStock) => {
            // Note: Le filtre stock faible peut être géré côté serveur ou client selon votre besoin
            // Pour l'instant, on le gère côté client dans ProductsList
            pagination.currentPage = 1;
            loadProducts({ skipLowStock: showLowStock });
        };

        // Initialisation
        onMounted(async () => {
            // Charger les produits et les catégories au montage du composant
            await Promise.all([
                loadProducts(),
                fetchCategories()
            ]);
        });

        return {
            // État
            products,
            lowStockProducts,
            categories,
            loading,
            pagination,
            showAddModal,
            showEditModal,
            showDeleteModal,
            productForm,
            productToDelete,
            deleteLoading,

            // Méthodes
            handlePageChange,
            handleSearchChange,
            handleCategoryChange,
            handleLowStockToggle,
            openAddModal,
            closeAddModal,
            closeEditModal,
            closeModals,
            editProduct,
            handleProductCreate,
            handleProductUpdate,
            handleProductSubmit,
            confirmDelete,
            deleteProduct
        };
    }
}
</script>
<style scoped>
.products-view {
    padding: 0 0 30px 0;
}

.insights-container {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.page-heading {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

/* Styles pour le modal de suppression */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
}

.modal-container {
    background-color: white;
    border-radius: 12px;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e2e8f0;
    background-color: white;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}

.modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #0A5C6B;
}

.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #64748b;
}

.close-btn:hover {
    color: #0A5C6B;
}

.modal-body {
    padding: 30px 20px;
    text-align: center;
}

.modal-body svg {
    color: #ef4444;
    margin-bottom: 20px;
}

.modal-body p {
    margin-bottom: 10px;
    font-size: 16px;
    color: #1e293b;
}

.text-danger {
    color: #ef4444;
    font-weight: 500;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 20px;
    border-top: 1px solid #e2e8f0;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-secondary {
    background-color: white;
    color: #1e293b;
    border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
    background-color: #f8fafc;
}

.btn-danger {
    background-color: #ef4444;
    color: white;
    border: none;
}

.btn-danger:hover:not(:disabled) {
    background-color: #dc2626;
}

.btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner-border {
    display: inline-block;
    width: 1em;
    height: 1em;
    border: 0.15em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border 0.75s linear infinite;
}

.spinner-border-sm {
    width: 1em;
    height: 1em;
}

@keyframes spinner-border {
    to {
        transform: rotate(360deg);
    }
}

.mr-1 {
    margin-right: 0.25rem;
}

@media (max-width: 768px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
}
</style>